import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import EmployeePlantTab from './EmployeePlantTab';
import EmployeeVehiclesTab from './EmployeeVehiclesTab';
import EmployeeVehicleTab from './EmployeeVehicleTab';
import EmployeeTrainingTab from './EmployeeTrainingTab';
import EmployeeLeaveTab from './EmployeeLeaveTab';
import EmployeeDocumentsTab from './EmployeeDocumentsTab';
import EmployeeTimesheetTab from './EmployeeTimesheetTab';
import { apiFetch } from './utils/api';

function EmployeeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  // /employees/new has no :id in its route, so id is undefined there.
  const isNew = !id || id === 'new';

  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Tab data
  const [plant, setPlant] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [training, setTraining] = useState([]);
  const [leave, setLeave] = useState([]);
  const [documents, setDocuments] = useState([]);
  const [timesheet, setTimesheet] = useState([]);
  const [bradfordFactor, setBradfordFactor] = useState(null);
  const [leaveBalance, setLeaveBalance] = useState(null);

  // Employee status and department options
  const [statusOptions, setStatusOptions] = useState([]);
  const [departmentOptions, setDepartmentOptions] = useState([]);

  const [employee, setEmployee] = useState({
    employee_number: '',
    first_name: '',
    last_name: '',
    date_of_birth: '',
    national_insurance: '',
    email: '',
    phone: '',
    mobile: '',
    address_line1: '',
    address_line2: '',
    city: '',
    county: '',
    postcode: '',
    job_title: '',
    department: '',
    employment_type: 'Full-time',
    contracted_hours: '',
    start_date: '',
    end_date: '',
    salary: '',
    salary_period: 'Annual',
    emergency_contact_name: '',
    emergency_contact_relationship: '',
    emergency_contact_phone: '',
    status: 'Active',
    annual_leave_entitlement: 28,
    photo_url: '',
    notes: ''
  });

  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  // Mobile access state
  const [mobileAccess, setMobileAccess] = useState(null);
  const [mobileLoading, setMobileLoading] = useState(false);
  const [mobileCopied, setMobileCopied] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [creatingMobile, setCreatingMobile] = useState(false);
  const [createResult, setCreateResult] = useState(null);
  const [createError, setCreateError] = useState('');
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [linkableUsers, setLinkableUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [linkingLogin, setLinkingLogin] = useState(false);
  const [unlinkingLogin, setUnlinkingLogin] = useState(false);
  const [linkError, setLinkError] = useState('');

  useEffect(() => {
    fetchStatusOptions();
    fetchDepartmentOptions();
    if (!isNew) {
      fetchEmployee();
      fetchPlant();
      fetchVehicles();
      fetchTraining();
      fetchLeave();
      fetchDocuments();
      fetchTimesheet();
      fetchBradfordFactor();
      fetchLeaveBalance();
      fetchMobileAccess();
    }
  }, [id]);

  const fetchStatusOptions = async () => {
    try {
      const response = await apiFetch('/api/custom-lists?list_type=employee_status');
      const data = await response.json();

      // If no custom statuses, use defaults
      if (data.length === 0) {
        setStatusOptions([
          { id: 1, list_value: 'Active' },
          { id: 2, list_value: 'On Leave' },
          { id: 3, list_value: 'Suspended' },
          { id: 4, list_value: 'Terminated' }
        ]);
      } else {
        setStatusOptions(data);
      }
    } catch (error) {
      console.error('Error fetching status options:', error);
      // Fallback to defaults
      setStatusOptions([
        { id: 1, list_value: 'Active' },
        { id: 2, list_value: 'On Leave' },
        { id: 3, list_value: 'Suspended' },
        { id: 4, list_value: 'Terminated' }
      ]);
    }
  };

  const fetchDepartmentOptions = async () => {
    try {
      const response = await apiFetch('/api/custom-lists?list_type=employee_department');
      const data = await response.json();

      // If no custom departments, use defaults
      if (data.length === 0) {
        setDepartmentOptions([
          { id: 1, list_value: 'Engineering' },
          { id: 2, list_value: 'Sales' },
          { id: 3, list_value: 'Administration' },
          { id: 4, list_value: 'Warehouse' },
          { id: 5, list_value: 'Management' }
        ]);
      } else {
        setDepartmentOptions(data);
      }
    } catch (error) {
      console.error('Error fetching department options:', error);
      // Fallback to defaults
      setDepartmentOptions([
        { id: 1, list_value: 'Engineering' },
        { id: 2, list_value: 'Sales' },
        { id: 3, list_value: 'Administration' },
        { id: 4, list_value: 'Warehouse' },
        { id: 5, list_value: 'Management' }
      ]);
    }
  };

  const fetchPlant = async () => {
    try {
      const response = await apiFetch(`/api/employees/${id}/plant`);
      const data = await response.json();
      setPlant(data);
    } catch (error) {
      console.error('Error fetching plant:', error);
    }
  };

  const fetchVehicles = async () => {
    try {
      const response = await apiFetch(`/api/employees/${id}/vehicles`);
      const data = await response.json();
      setVehicles(data);
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    }
  };

  const fetchTraining = async () => {
    try {
      const response = await apiFetch(`/api/employees/${id}/training`);
      const data = await response.json();
      setTraining(data);
    } catch (error) {
      console.error('Error fetching training:', error);
    }
  };

  const fetchLeave = async () => {
    try {
      const response = await apiFetch(`/api/employees/${id}/leave`);
      const data = await response.json();
      setLeave(data);
    } catch (error) {
      console.error('Error fetching leave:', error);
    }
  };

  const fetchDocuments = async () => {
    try {
      const response = await apiFetch(`/api/employees/${id}/documents`);
      const data = await response.json();
      setDocuments(data);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const fetchTimesheet = async () => {
    try {
      const response = await apiFetch(`/api/employees/${id}/timesheet`);
      const data = await response.json();
      setTimesheet(data);
    } catch (error) {
      console.error('Error fetching timesheet:', error);
    }
  };

  const fetchBradfordFactor = async () => {
    try {
      const response = await apiFetch(`/api/bradford-factor/${id}`);
      const data = await response.json();
      setBradfordFactor(data);
    } catch (error) {
      console.error('Error fetching Bradford Factor:', error);
    }
  };

  const fetchLeaveBalance = async () => {
    try {
      const response = await apiFetch(`/api/leave-balance/${id}`);
      const data = await response.json();
      setLeaveBalance(data);
    } catch (error) {
      console.error('Error fetching leave balance:', error);
    }
  };

  const fetchEmployee = async () => {
    try {
      const response = await apiFetch(`/api/employees/${id}`);
      const data = await response.json();

      if (data.date_of_birth) data.date_of_birth = data.date_of_birth.split('T')[0];
      if (data.start_date) data.start_date = data.start_date.split('T')[0];
      if (data.end_date) data.end_date = data.end_date.split('T')[0];

      setEmployee(data);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching employee:', error);
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    console.log('Saving employee:', employee);
    try {
      const url = isNew
        ? '/api/employees'
        : `/api/employees/${id}`;

      const response = await apiFetch(url, {
        method: isNew ? 'POST' : 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(employee)
      });

      const data = await response.json();
      console.log('Save response:', data);

      if (!response.ok) {
        alert(`Error saving employee: ${data.error || 'Unknown error'}`);
        setSaving(false);
        return;
      }

      if (isNew) {
        navigate(`/employees/${data.id}`);
      } else {
        alert('Employee saved successfully');
        fetchEmployee();
      }
    } catch (error) {
      console.error('Error saving employee:', error);
      alert('Error saving employee');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!window.confirm(`Delete employee ${employee.first_name} ${employee.last_name}?`)) return;

    setDeleting(true);
    try {
      await apiFetch(`/api/employees/${id}`, {
        method: 'DELETE'
      });
      navigate('/employees');
    } catch (error) {
      console.error('Error deleting employee:', error);
      alert('Error deleting employee');
      setDeleting(false);
    }
  };

  // Mobile access functions
  const fetchMobileAccess = async () => {
    if (isNew) return;
    setMobileLoading(true);
    try {
      const [accessResponse, loginsResponse] = await Promise.all([
        apiFetch(`/api/employees/${id}/mobile-access`),
        apiFetch(`/api/employees/${id}/mobile-access/logins`),
      ]);
      if (accessResponse.ok) {
        setMobileAccess(await accessResponse.json());
      }
      if (loginsResponse.ok) {
        const data = await loginsResponse.json();
        setLinkableUsers(Array.isArray(data.users) ? data.users : []);
      }
    } catch (error) {
      console.error('Error fetching mobile access:', error);
    } finally {
      setMobileLoading(false);
    }
  };

  const createMobileAccess = async () => {
    setCreatingMobile(true);
    setCreateError('');
    setCreateResult(null);
    try {
      const response = await apiFetch(`/api/employees/${id}/mobile-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword, is_mobile_user: true })
      });
      const data = await response.json();
      if (response.ok) {
        setCreateResult(data);
        setNewPassword('');
        fetchMobileAccess();
      } else {
        setCreateError(data.error || 'Failed to create mobile access');
      }
    } catch (error) {
      setCreateError('Failed to create mobile access');
    } finally {
      setCreatingMobile(false);
    }
  };

  const toggleMobileAccess = async (enabled) => {
    setLinkError('');
    try {
      const response = await apiFetch(`/api/employees/${id}/mobile-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ is_mobile_user: enabled })
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        fetchMobileAccess();
      } else {
        setLinkError(data.error || 'Could not update mobile access');
      }
    } catch (error) {
      setLinkError('Could not update mobile access');
    }
  };

  const linkExistingLogin = async () => {
    setLinkingLogin(true);
    setLinkError('');
    try {
      const response = await apiFetch(`/api/employees/${id}/mobile-access/link`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ userId: Number(selectedUserId) })
      });
      const data = await response.json();
      if (response.ok) {
        setSelectedUserId('');
        fetchMobileAccess();
      } else {
        setLinkError(data.error || 'Could not link that login');
      }
    } catch (error) {
      setLinkError('Could not link that login');
    } finally {
      setLinkingLogin(false);
    }
  };

  const unlinkLogin = async () => {
    if (!window.confirm('Unlink this login? This person will not be able to sign into the engineer app until a login is linked again.')) return;
    setUnlinkingLogin(true);
    setLinkError('');
    try {
      const response = await apiFetch(`/api/employees/${id}/mobile-access/link`, {
        method: 'DELETE'
      });
      const data = await response.json().catch(() => ({}));
      if (response.ok) {
        fetchMobileAccess();
      } else {
        setLinkError(data.error || 'Could not unlink that login');
      }
    } catch (error) {
      setLinkError('Could not unlink that login');
    } finally {
      setUnlinkingLogin(false);
    }
  };

  const resetPassword = async () => {
    setResettingPassword(true);
    setResetResult(null);
    try {
      const response = await apiFetch(`/api/employees/${id}/mobile-access`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password: newPassword })
      });
      const data = await response.json();
      if (response.ok) {
        setResetResult({ success: true, message: 'Password reset successfully' });
        setNewPassword('');
        setShowResetPassword(false);
      } else {
        setResetResult({ success: false, message: data.error || 'Failed to reset password' });
      }
    } catch (error) {
      setResetResult({ success: false, message: 'Failed to reset password' });
    } finally {
      setResettingPassword(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 2MB for base64)
    if (file.size > 2 * 1024 * 1024) {
      alert('Photo must be less than 2MB');
      return;
    }

    setUploadingPhoto(true);

    // Convert to base64
    const reader = new FileReader();
    reader.onload = async (event) => {
      const base64Photo = event.target.result;

      try {
        // Save directly to employee record
        const response = await apiFetch(`/api/employees/${id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({...employee, photo_url: base64Photo})
        });

        if (response.ok) {
          setEmployee({...employee, photo_url: base64Photo});
          alert('Photo uploaded successfully');
        } else {
          alert('Error saving photo');
        }
      } catch (error) {
        console.error('Error uploading photo:', error);
        alert('Error uploading photo');
      } finally {
        setUploadingPhoto(false);
      }
    };

    reader.readAsDataURL(file);
  };

  const handlePhotoDownload = () => {
    if (!employee.photo_url) {
      alert('No photo to download');
      return;
    }

    // Convert base64 to downloadable file
    const fileName = `${employee.first_name}_${employee.last_name}_ID_Photo.jpg`;
    const a = document.createElement('a');
    a.href = employee.photo_url;
    a.download = fileName;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-xl text-gray-600">Loading employee...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white shadow-md sticky top-0 z-10" style={{ height: '116px' }}>
        <div className="max-w-full mx-auto px-6 h-full flex flex-col justify-center">
          <div className="flex items-center justify-between">
            <button
              onClick={() => navigate('/employees')}
              className="text-white hover:text-blue-100 transition text-lg font-semibold"
            >
              ⬅ Back
            </button>
            <div className="flex-1 text-center">
              <h1 className="text-4xl font-bold">
                {isNew ? 'New Employee' : `${employee.first_name} ${employee.last_name}`}
              </h1>
              <span className="text-sm text-blue-100">
                {!isNew && `${employee.job_title || 'Employee'} • ${employee.department || ''}`}
              </span>
            </div>
            <div className="flex items-center space-x-2">
              {!isNew && (
                <button
                  onClick={handleDelete}
                  disabled={deleting}
                  className="px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded text-sm font-medium transition disabled:opacity-50"
                >
                  {deleting ? '⏳' : '🗑️'}
                </button>
              )}
              <button
                onClick={handleSave}
                disabled={saving}
                className="px-4 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded-lg font-medium transition text-sm shadow-sm disabled:opacity-50"
              >
                {saving ? '⏳ Saving...' : '💾 Save'}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="max-w-full px-4 py-3">
        <div className="bg-gray-50 rounded-xl shadow-sm p-2 border border-gray-200 inline-flex space-x-1 overflow-x-auto max-w-full">
            <button
              onClick={() => setActiveTab('overview')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition-all ${
                activeTab === 'overview'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              Overview
            </button>
            {/* The other tabs need a saved staff member, so they appear after the first save. */}
            {!isNew && (<>
            <button
              onClick={() => setActiveTab('plant')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === 'plant'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              Company Plant
            </button>
            <button
              onClick={() => setActiveTab('vehicles')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === 'vehicles'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              Vehicles
            </button>
            <button
              onClick={() => setActiveTab('training')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === 'training'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              Training
            </button>
            <button
              onClick={() => setActiveTab('leave')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === 'leave'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              Leave
            </button>
            <button
              onClick={() => setActiveTab('documents')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === 'documents'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              Documents
            </button>
            <button
              onClick={() => setActiveTab('timesheet')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === 'timesheet'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              Time & Attendance
            </button>
            <button
              onClick={() => setActiveTab('gps')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === 'gps'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              GPS Tracking
            </button>
            <button
              onClick={() => setActiveTab('mobile')}
              className={`px-6 py-2.5 rounded-lg text-sm font-semibold transition ${
                activeTab === 'mobile'
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300 hover:text-gray-900'
              }`}
            >
              📱 Mobile
            </button>
            </>)}
        </div>
      </div>

      {/* Main Content */}
      <div className="px-4 pb-3">
        <div className="rounded-xl bg-gray-100 border-4 border-gray-400 shadow-[inset_6px_6px_12px_rgba(0,0,0,0.3),inset_-4px_-4px_8px_rgba(255,255,255,0.9)] overflow-auto max-h-[calc(100vh-135px)] min-h-[650px]">
          <div className="p-8">
        {activeTab === 'overview' && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            {/* Bradford Factor Badge */}
            {bradfordFactor && bradfordFactor.bradfordScore > 0 && (
              <div className={`mb-6 p-4 rounded-lg border-2 ${
                bradfordFactor.riskColor === 'red' ? 'bg-red-50 border-red-500' :
                bradfordFactor.riskColor === 'orange' ? 'bg-orange-50 border-orange-500' :
                bradfordFactor.riskColor === 'yellow' ? 'bg-yellow-50 border-yellow-500' :
                'bg-green-50 border-green-500'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Bradford Factor Score: {bradfordFactor.bradfordScore}
                    </h3>
                    <p className="text-sm text-gray-700 mt-1">
                      {bradfordFactor.spells} absence period{bradfordFactor.spells !== 1 ? 's' : ''} × {bradfordFactor.totalDays} day{bradfordFactor.totalDays !== 1 ? 's' : ''} = {bradfordFactor.spells}² × {bradfordFactor.totalDays}
                    </p>
                    <p className="text-xs text-gray-600 mt-1">
                      Current calendar year ({bradfordFactor.year}) • Unauthorized absences only
                    </p>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-bold ${
                    bradfordFactor.riskColor === 'red' ? 'bg-red-600 text-white' :
                    bradfordFactor.riskColor === 'orange' ? 'bg-orange-600 text-white' :
                    bradfordFactor.riskColor === 'yellow' ? 'bg-yellow-600 text-white' :
                    'bg-green-600 text-white'
                  }`}>
                    {bradfordFactor.riskLevel.toUpperCase()}
                  </div>
                </div>
                {bradfordFactor.riskLevel !== 'low' && (
                  <div className="mt-3 pt-3 border-t border-gray-300">
                    <p className="text-sm font-medium text-gray-900">
                      {bradfordFactor.riskLevel === 'critical' && '🚨 CRITICAL: Formal intervention required'}
                      {bradfordFactor.riskLevel === 'high' && '⚠️ HIGH RISK: Review and action needed'}
                      {bradfordFactor.riskLevel === 'moderate' && '👀 MODERATE: Monitor attendance'}
                    </p>
                  </div>
                )}
              </div>
            )}

            {/* Annual Leave Balance */}
            {leaveBalance && (
              <div className={`mb-6 p-4 rounded-lg border-2 ${
                leaveBalance.status === 'overdrawn' ? 'bg-red-50 border-red-500' :
                leaveBalance.status === 'low' ? 'bg-yellow-50 border-yellow-500' :
                'bg-blue-50 border-blue-500'
              }`}>
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Annual Leave Balance ({leaveBalance.year})
                    </h3>
                    <div className="flex items-center gap-6 mt-2">
                      <div>
                        <p className="text-sm text-gray-600">Entitlement</p>
                        <p className="text-2xl font-bold text-gray-900">{leaveBalance.entitlement} days</p>
                        {leaveBalance.isProRata && (
                          <p className="text-xs text-gray-600 mt-1">{leaveBalance.proRataNote}</p>
                        )}
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Taken</p>
                        <p className="text-2xl font-bold text-blue-600">{leaveBalance.taken} days</p>
                      </div>
                      {leaveBalance.pending > 0 && (
                        <div>
                          <p className="text-sm text-gray-600">Pending</p>
                          <p className="text-2xl font-bold text-yellow-600">{leaveBalance.pending} days</p>
                        </div>
                      )}
                      <div>
                        <p className="text-sm text-gray-600">Remaining</p>
                        <p className={`text-2xl font-bold ${
                          leaveBalance.remaining < 0 ? 'text-red-600' :
                          leaveBalance.remaining < 5 ? 'text-yellow-600' :
                          'text-green-600'
                        }`}>
                          {leaveBalance.remaining} days
                        </p>
                      </div>
                    </div>
                  </div>
                  <div className={`px-4 py-2 rounded-full text-sm font-bold ${
                    leaveBalance.status === 'overdrawn' ? 'bg-red-600 text-white' :
                    leaveBalance.status === 'low' ? 'bg-yellow-600 text-white' :
                    'bg-green-600 text-white'
                  }`}>
                    {leaveBalance.status === 'overdrawn' ? 'OVERDRAWN' :
                     leaveBalance.status === 'low' ? 'LOW' : 'OK'}
                  </div>
                </div>
                {leaveBalance.status === 'overdrawn' && (
                  <div className="mt-3 pt-3 border-t border-red-300">
                    <p className="text-sm font-medium text-red-900">
                      ⚠️ Employee has taken more leave than entitled this year
                    </p>
                  </div>
                )}
                {leaveBalance.pending > 0 && leaveBalance.availableIfPendingApproved < 0 && (
                  <div className="mt-3 pt-3 border-t border-yellow-300">
                    <p className="text-sm font-medium text-yellow-900">
                      ⚠️ Pending requests exceed available balance
                    </p>
                  </div>
                )}
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Personal Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Personal Details</h3>

                {/* ID Photo */}
                <div className="flex items-start space-x-4 mb-4 pb-4 border-b border-gray-200">
                  <div className="flex-shrink-0">
                    {employee.photo_url ? (
                      <img
                        src={employee.photo_url}
                        alt="Employee photo"
                        className="w-24 h-24 rounded-lg object-cover border-2 border-gray-300"
                      />
                    ) : (
                      <div className="w-24 h-24 rounded-lg bg-gray-200 flex items-center justify-center border-2 border-gray-300">
                        <span className="text-gray-400 text-xs text-center">No photo</span>
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="block text-sm font-medium text-gray-700 mb-2">ID Photo</label>
                    <div className="flex items-center space-x-2">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handlePhotoUpload}
                        disabled={uploadingPhoto || isNew}
                        className="text-sm text-gray-600 file:mr-4 file:py-2 file:px-4 file:rounded file:border-0 file:text-sm file:font-medium file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200 disabled:opacity-50"
                      />
                      {employee.photo_url && !isNew && (
                        <button
                          onClick={handlePhotoDownload}
                          className="px-3 py-2 bg-gray-300 hover:bg-gray-400 text-black rounded text-sm font-medium transition-colors"
                        >
                          ⬇ Download
                        </button>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">JPG, PNG or GIF (max 5MB)</p>
                    {uploadingPhoto && <p className="text-xs text-blue-600 mt-1">Uploading...</p>}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employee Number</label>
                  <input
                    type="text"
                    value={employee.employee_number}
                    onChange={(e) => setEmployee({...employee, employee_number: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">First Name *</label>
                  <input
                    type="text"
                    value={employee.first_name}
                    onChange={(e) => setEmployee({...employee, first_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Last Name *</label>
                  <input
                    type="text"
                    value={employee.last_name}
                    onChange={(e) => setEmployee({...employee, last_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date of Birth</label>
                  <input
                    type="date"
                    value={employee.date_of_birth}
                    onChange={(e) => setEmployee({...employee, date_of_birth: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">National Insurance</label>
                  <input
                    type="text"
                    value={employee.national_insurance}
                    onChange={(e) => setEmployee({...employee, national_insurance: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    placeholder="AB123456C"
                  />
                </div>
              </div>

              {/* Contact Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Contact Details</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                  <input
                    type="email"
                    value={employee.email}
                    onChange={(e) => setEmployee({...employee, email: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={employee.phone}
                    onChange={(e) => setEmployee({...employee, phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Mobile</label>
                  <input
                    type="tel"
                    value={employee.mobile}
                    onChange={(e) => setEmployee({...employee, mobile: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 1</label>
                  <input
                    type="text"
                    value={employee.address_line1}
                    onChange={(e) => setEmployee({...employee, address_line1: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Address Line 2</label>
                  <input
                    type="text"
                    value={employee.address_line2}
                    onChange={(e) => setEmployee({...employee, address_line2: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">City</label>
                    <input
                      type="text"
                      value={employee.city}
                      onChange={(e) => setEmployee({...employee, city: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">County</label>
                    <input
                      type="text"
                      value={employee.county}
                      onChange={(e) => setEmployee({...employee, county: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Postcode</label>
                  <input
                    type="text"
                    value={employee.postcode}
                    onChange={(e) => setEmployee({...employee, postcode: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Employment Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Employment Details</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                  <input
                    type="text"
                    value={employee.job_title}
                    onChange={(e) => setEmployee({...employee, job_title: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                  <select
                    value={employee.department}
                    onChange={(e) => setEmployee({...employee, department: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  >
                    <option value="">Select department...</option>
                    {departmentOptions.map((option) => (
                      <option key={option.id} value={option.list_value}>
                        {option.list_value}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Employment Type</label>
                  <select
                    value={employee.employment_type}
                    onChange={(e) => setEmployee({...employee, employment_type: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  >
                    <option value="Full-time">Full-time</option>
                    <option value="Part-time">Part-time</option>
                    <option value="Contract">Contract</option>
                    <option value="Apprentice">Apprentice</option>
                  </select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Contracted Hours/week</label>
                    <input
                      type="number"
                      step="0.5"
                      value={employee.contracted_hours}
                      onChange={(e) => setEmployee({...employee, contracted_hours: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      placeholder="37.5"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Holiday Entitlement (days)</label>
                    <input
                      type="number"
                      step="0.5"
                      value={employee.annual_leave_entitlement || 28}
                      onChange={(e) => setEmployee({...employee, annual_leave_entitlement: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      placeholder="28"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                    <input
                      type="date"
                      value={employee.start_date}
                      onChange={(e) => setEmployee({...employee, start_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
                    <input
                      type="date"
                      value={employee.end_date}
                      onChange={(e) => setEmployee({...employee, end_date: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Salary</label>
                    <div className="relative">
                      <span className="absolute left-3 top-2 text-gray-500">£</span>
                      <input
                        type="number"
                        step="0.01"
                        value={employee.salary}
                        onChange={(e) => setEmployee({...employee, salary: e.target.value})}
                        className="w-full pl-7 pr-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                    <select
                      value={employee.salary_period}
                      onChange={(e) => setEmployee({...employee, salary_period: e.target.value})}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    >
                      <option value="Hourly">Hourly</option>
                      <option value="Daily">Daily</option>
                      <option value="Monthly">Monthly</option>
                      <option value="Annual">Annual</option>
                    </select>
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                  <select
                    value={employee.status || ''}
                    onChange={(e) => setEmployee({...employee, status: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  >
                    <option value="">Select status...</option>
                    {statusOptions.map((option) => (
                      <option key={option.id} value={option.list_value}>
                        {option.list_value}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Emergency Contact</h3>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                  <input
                    type="text"
                    value={employee.emergency_contact_name}
                    onChange={(e) => setEmployee({...employee, emergency_contact_name: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Relationship</label>
                  <input
                    type="text"
                    value={employee.emergency_contact_relationship}
                    onChange={(e) => setEmployee({...employee, emergency_contact_relationship: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                    placeholder="Spouse, Parent, Sibling, etc."
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Phone</label>
                  <input
                    type="tel"
                    value={employee.emergency_contact_phone}
                    onChange={(e) => setEmployee({...employee, emergency_contact_phone: e.target.value})}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>

                <div className="pt-4">
                  <label className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                  <textarea
                    value={employee.notes}
                    onChange={(e) => setEmployee({...employee, notes: e.target.value})}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-slate-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Company Plant Tab */}
        {activeTab === 'plant' && <EmployeePlantTab employeeId={id} plant={plant} onRefresh={fetchPlant} />}

        {/* Vehicles Tab */}
        {activeTab === 'vehicles' && <EmployeeVehiclesTab employeeId={id} vehicles={vehicles} onRefresh={fetchVehicles} />}

        {/* Training Tab */}
        {activeTab === 'training' && <EmployeeTrainingTab employeeId={id} training={training} onRefresh={fetchTraining} />}

        {/* Leave Tab */}
        {activeTab === 'leave' && <EmployeeLeaveTab employeeId={id} leave={leave} onRefresh={fetchLeave} />}

        {/* Documents Tab */}
        {activeTab === 'documents' && (
          <EmployeeDocumentsTab
            employeeId={id}
            documents={documents}
            onRefresh={fetchDocuments}
          />
        )}

        {/* Timesheet Tab */}
        {activeTab === 'timesheet' && <EmployeeTimesheetTab employeeId={id} timesheet={timesheet} onRefresh={fetchTimesheet} />}

        {/* GPS Tracking Tab */}
        {activeTab === 'gps' && <EmployeeVehicleTab employeeId={id} />}

        {/* Mobile Access Tab */}
        {activeTab === 'mobile' && !isNew && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="max-w-2xl">
              <div className="flex items-center justify-between mb-6">
                <h3 className="text-lg font-semibold text-gray-900">📱 Mobile App Access</h3>
                <button
                  type="button"
                  onClick={() => fetchMobileAccess()}
                  className="text-sm text-blue-600 hover:text-blue-800 underline"
                >
                  Refresh
                </button>
              </div>

              {mobileLoading ? (
                <div className="flex items-center justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  <span className="ml-3 text-gray-500">Loading...</span>
                </div>
              ) : (
                <div className="space-y-6">
                  <div className={`p-4 rounded-lg border ${mobileAccess?.engineer_app_ready ? 'bg-green-50 border-green-200' : 'bg-amber-50 border-amber-200'}`}>
                    <p className={`font-medium ${mobileAccess?.engineer_app_ready ? 'text-green-900' : 'text-amber-900'}`}>
                      {mobileAccess?.engineer_app_ready ? 'Engineer app access is set up' : 'Engineer app access is not set up'}
                    </p>
                    <p className="text-sm text-gray-600 mt-1">
                      {mobileAccess?.engineer_app_ready
                        ? 'This staff record is linked to a login in this company. They can sign into the engineer app with that email and password, then set a PIN on the phone.'
                        : 'Create a login or link an existing one in this company. Until that is done, the engineer app will refuse this person.'}
                    </p>
                  </div>

                  {mobileAccess?.linked_user && (
                    <div className="p-4 border border-gray-200 rounded-lg">
                      <p className="text-sm font-medium text-gray-900 mb-1">Linked login</p>
                      <p className="text-lg font-mono font-semibold text-gray-900">{mobileAccess.linked_user.email}</p>
                      <button
                        type="button"
                        onClick={unlinkLogin}
                        disabled={unlinkingLogin}
                        className="mt-3 px-4 py-2 text-sm font-medium bg-white text-red-700 border border-red-300 rounded hover:bg-red-50 disabled:opacity-50"
                      >
                        {unlinkingLogin ? 'Unlinking...' : 'Unlink login'}
                      </button>
                    </div>
                  )}

                  {mobileAccess?.has_login_account ? (
                <div className="space-y-6">
                  {/* Status Toggle */}
                  <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg border border-gray-200">
                    <div>
                      <p className="font-medium text-gray-900">Mobile App Access</p>
                      <p className="text-sm text-gray-500">{mobileAccess.message}</p>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={mobileAccess.has_mobile_access}
                        onChange={(e) => toggleMobileAccess(e.target.checked)}
                        className="sr-only peer"
                      />
                      <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                    </label>
                  </div>

                  {mobileAccess.has_mobile_access && (
                    <>
                      {/* Login ID Card */}
                      <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                        <p className="text-sm font-medium text-blue-900 mb-2">Login Credentials</p>
                        <div className="flex items-center gap-3 bg-white rounded p-3 border border-blue-100">
                          <div className="flex-1">
                            <p className="text-xs text-gray-500">Login ID (Email)</p>
                            <p className="text-lg font-mono font-semibold text-gray-900">{mobileAccess.login_id}</p>
                          </div>
                          <button
                            type="button"
                            onClick={() => {navigator.clipboard.writeText(mobileAccess.login_id); setMobileCopied(true); setTimeout(() => setMobileCopied(false), 2000);}}
                            className="px-3 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                          >
                            {mobileCopied ? '✓ Copied!' : '📋 Copy'}
                          </button>
                        </div>
                        <p className="text-xs text-blue-700 mt-2">Share this login ID with the engineer so they can sign into the mobile app.</p>
                      </div>

                      {/* Reset Password */}
                      <div className="p-4 border border-gray-200 rounded-lg">
                        <p className="font-medium text-gray-900 mb-3">🔑 Reset Password</p>
                        {!showResetPassword ? (
                          <button
                            type="button"
                            onClick={() => {setShowResetPassword(true); setNewPassword(''); setResetResult(null);}}
                            className="px-4 py-2 text-sm font-medium bg-amber-100 text-amber-800 border border-amber-300 rounded hover:bg-amber-200 transition-colors"
                          >
                            Reset Password
                          </button>
                        ) : (
                          <div className="space-y-3">
                            <input
                              type="text"
                              placeholder="Enter new password (min 6 characters)"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                            />
                            <div className="flex items-center gap-3">
                              <button
                                type="button"
                                onClick={resetPassword}
                                disabled={resettingPassword || newPassword.length < 6}
                                className="px-4 py-2 text-sm font-medium bg-amber-600 text-white rounded hover:bg-amber-700 disabled:opacity-50 transition-colors"
                              >
                                {resettingPassword ? 'Resetting...' : 'Set New Password'}
                              </button>
                              <button
                                type="button"
                                onClick={() => {setShowResetPassword(false); setNewPassword(''); setResetResult(null);}}
                                className="text-sm text-gray-500 hover:text-gray-700"
                              >
                                Cancel
                              </button>
                            </div>
                            {resetResult && (
                              <p className={`text-sm font-medium ${resetResult.success ? 'text-green-600' : 'text-red-600'}`}>
                                {resetResult.success ? '✓' : '✗'} {resetResult.message}
                              </p>
                            )}
                          </div>
                        )}
                      </div>

                      {/* Last Login */}
                      {mobileAccess.last_login && (
                        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
                          <p className="text-sm text-gray-600">
                            Last mobile login: <span className="font-medium text-gray-900">
                              {new Date(mobileAccess.last_login).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                            </span>
                          </p>
                        </div>
                      )}
                    </>
                  )}
                </div>
              ) : (
                <div className="space-y-4">
                  <p className="text-gray-600">No login account yet. Create one to give this employee mobile app access.</p>
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <p className="text-sm text-amber-800">
                      <strong>Note:</strong> The employee must have an email address set in their profile before you can create a mobile login.
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Password for mobile login</label>
                      <input
                        type="text"
                        placeholder="Enter a password (min 6 characters)"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={createMobileAccess}
                      disabled={creatingMobile || !newPassword || newPassword.length < 6}
                      className="px-6 py-2.5 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {creatingMobile ? 'Creating...' : '✅ Create Login & Enable Mobile Access'}
                    </button>
                    {createResult && (
                      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                        <p className="text-sm font-semibold text-green-800">{createResult.message}</p>
                        <div className="mt-2 flex items-center gap-2 bg-white rounded p-3 border border-green-100">
                          <p className="text-lg font-mono text-green-900">{createResult.login_id}</p>
                          <button
                            type="button"
                            onClick={() => {navigator.clipboard.writeText(createResult.login_id); setMobileCopied(true); setTimeout(() => setMobileCopied(false), 2000);}}
                            className="text-sm text-blue-600 hover:text-blue-800"
                          >
                            {mobileCopied ? '✓ Copied!' : '📋 Copy'}
                          </button>
                        </div>
                        <p className="text-sm text-green-700 mt-2">Share this login ID and the password with the engineer so they can sign into the mobile app.</p>
                      </div>
                    )}
                    {createError && <p className="text-sm text-red-600">{createError}</p>}
                  </div>
                </div>
                  )}

                  <div className="p-4 border border-gray-200 rounded-lg space-y-3">
                    <p className="font-medium text-gray-900">Link existing login</p>
                    <p className="text-sm text-gray-600">Only logins in this company are listed. A login already used by another staff record cannot be linked again.</p>
                    <select
                      value={selectedUserId}
                      onChange={(e) => setSelectedUserId(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="">Choose a login in this company</option>
                      {linkableUsers.map((user) => {
                        const taken = user.linked_employee_id && String(user.linked_employee_id) !== String(id);
                        return (
                          <option key={user.id} value={user.id} disabled={taken}>
                            {user.email}{taken ? ' (already linked)' : ''}
                          </option>
                        );
                      })}
                    </select>
                    <button
                      type="button"
                      onClick={linkExistingLogin}
                      disabled={linkingLogin || !selectedUserId}
                      className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {linkingLogin ? 'Linking...' : 'Link existing login'}
                    </button>
                    {linkError && <p className="text-sm text-red-600">{linkError}</p>}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default EmployeeDetail;
